import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_CREATION_ROUTE,
  H1,
  H2,
  LOGIN_ROUTE,
  MODE_ALL,
  PROVIDER_INFO,
  P_REGULAR,
  SECONDARY_MAIN,
} from "../../../Constants/CommonConstants";
import {
  ACCESS_CODE_EXPIRATION_ERROR,
  ACCESS_CODE_IS_VERIFIED_FALSE_ERROR,
  ACCESS_CODE_NAME,
  CONFIRM_SITE_ACCESS_FORM_ID,
  CONFIRM_SITE_ACCESS_HEADING,
  CONFIRM_SITE_ACCESS_TEXT,
  LOCATION_NPI_NAME,
} from "../../../Constants/ConfirmSiteAccessConstants";
import {
  SLICE_PROVIDER_ACCOUNT_CREATION,
  THUNK_POST_VERIFY_PROVIDER_DETAILS,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearProviderAccountCreationApiResponse,
  postVerifyProviderDetails,
  saveSiteAccess,
} from "../../../Features/ProviderAccountCreationSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageProviderAccountCreationSelector } from "../../../Selectors/ProviderAccountCreationSelector";
import ConfirmSiteAccessForm from "./ConfirmSiteAccessForm";

/**
 * @description Confirm Site Access screen allows the user to enter the access code and location NPI details and navigate user based on the entered details
 * @returns {ReactNode} Renders the Confirm Site Access Screen which has the heading, subtext and input fields
 */
const ConfirmSiteAccess = () => {
  const intl = useIntl();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { accessCode, locationNPI, confirmSiteAccessDetails } = useSelector(
    (state) => manageProviderAccountCreationSelector(state)
  );

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_PROVIDER_ACCOUNT_CREATION,
      THUNK_POST_VERIFY_PROVIDER_DETAILS
    )
  );

  const { isSuccess } = apiResponses;

  const { isAccountCreated, isVerified, isAccessCodeExpired } =
    confirmSiteAccessDetails;

  const methods = useForm({
    mode: MODE_ALL,
    defaultValues: {
      [ACCESS_CODE_NAME]: accessCode,
      [LOCATION_NPI_NAME]: locationNPI,
    },
  });

  const accessCodeFormValue = useWatch({
    name: ACCESS_CODE_NAME,
    control: methods.control,
  });

  const locationNPIFormValue = useWatch({
    name: LOCATION_NPI_NAME,
    control: methods.control,
  });

  const onSubmit = (data) => {
    const payload = {
      accessCode: data.accessCode,
      npiId: data.locationNPI,
    };
    dispatch(saveSiteAccess(data));
    dispatch(postVerifyProviderDetails(payload));
  };

  /**
   * @description useEffect to handle once the success is received from the API call.
   */
  useEffect(() => {
    if (isSuccess) {
      dispatch(
        clearProviderAccountCreationApiResponse(
          THUNK_POST_VERIFY_PROVIDER_DETAILS
        )
      );
      if (isAccessCodeExpired) {
        setShowError(true);
        setErrorMessage(ACCESS_CODE_EXPIRATION_ERROR);
        return;
      }
      if (isVerified) {
        navigate(`/${ACCOUNT_CREATION_ROUTE}/${PROVIDER_INFO}`);
        return;
      }
      if (isAccountCreated) {
        navigate(`/${LOGIN_ROUTE}`);
        return;
      }
      setShowError(true);
      setErrorMessage(ACCESS_CODE_IS_VERIFIED_FALSE_ERROR);
    }
  }, [
    dispatch,
    isAccessCodeExpired,
    isAccountCreated,
    isSuccess,
    isVerified,
    navigate,
  ]);

  /**
   * @description useEffect to clear out the error when the access code is changed
   */
  useEffect(() => {
    setShowError(false);
    setErrorMessage("");
  }, [accessCodeFormValue, locationNPIFormValue]);

  return (
    <Stack spacing={{ xs: 3, md: 3 }}>
      <Typography
        component={H1}
        variant={H2}
        color={SECONDARY_MAIN}
        id={CONFIRM_SITE_ACCESS_HEADING}
      >
        {intl.formatMessage({ id: CONFIRM_SITE_ACCESS_HEADING })}
      </Typography>
      <Stack spacing={{ xs: 3, md: 6 }}>
        <Typography variant={P_REGULAR} id={CONFIRM_SITE_ACCESS_TEXT}>
          {intl.formatMessage({ id: CONFIRM_SITE_ACCESS_TEXT })}
        </Typography>
        <FormProvider {...methods}>
          <form
            id={CONFIRM_SITE_ACCESS_FORM_ID}
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <ConfirmSiteAccessForm
              showError={showError}
              errorMessage={errorMessage}
            />
          </form>
        </FormProvider>
      </Stack>
    </Stack>
  );
};

export default ConfirmSiteAccess;
